/**
 * The outcome of racing a promise against a deadline.
 *
 * Mirrors the shape of `Promise.allSettled` so a caller can branch on a single
 * discriminant, with `timeout` as the third case: the promise is still pending
 * and the caller has decided to stop waiting for it.
 */
export type TimeoutOutcome<T> =
  | { status: 'fulfilled'; value: T }
  | { status: 'rejected'; reason: unknown }
  | { status: 'timeout' };

/**
 * Resolve once `promise` settles or once `timeoutMs` elapses, whichever is
 * first.
 *
 * The returned promise NEVER rejects - a rejection is reported as
 * `{ status: 'rejected' }` - so code that must keep going whatever happens
 * (a UI that has to render) cannot be taken down by the work it waited on.
 *
 * Timing out does not cancel the underlying promise, because promises are not
 * cancellable; it only releases the caller. A late result is absorbed here
 * instead of surfacing as an unhandled rejection.
 */
export const settleWithTimeout = <T>(
  promise: Promise<T>,
  timeoutMs: number
): Promise<TimeoutOutcome<T>> => {
  const settled: Promise<TimeoutOutcome<T>> = promise.then(
    (value) => ({ status: 'fulfilled' as const, value }),
    (reason) => ({ status: 'rejected' as const, reason })
  );

  return new Promise<TimeoutOutcome<T>>((resolve) => {
    const timer = setTimeout(() => resolve({ status: 'timeout' }), timeoutMs);

    settled.then((outcome) => {
      // Releasing the deadline matters as much as resolving: an uncleared timer
      // keeps the event loop (and a Jest run) alive for the full timeout.
      clearTimeout(timer);
      resolve(outcome);
    });
  });
};
